// components/forms/MedicationForm.tsx
import React from 'react';
import { Plus, Check, Clock, Calendar } from 'lucide-react';
import type { Medication, QuantityUnit, DoseUnit, Frequency, Interval, DurationUnit } from '../../types/medication';
import { QUANTITY_UNITS, DOSE_UNITS, FREQUENCIES, INTERVALS, DURATION_UNITS } from '../../constants/medication';

interface MedicationFormProps {
  currentMedication: Medication;
  isEditing: boolean;
  onMedicationChange: (field: keyof Medication, value: string | number) => void;
  onSubmit: () => void;
  onCancel?: () => void;
}

const MedicationForm: React.FC<MedicationFormProps> = ({
  currentMedication,
  isEditing,
  onMedicationChange,
  onSubmit,
  onCancel
}) => {
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    field: keyof Medication
  ) => {
    const value = e.target.type === "number" ? Number(e.target.value) : e.target.value;
    onMedicationChange(field, value);
  };

  return (
    <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-lg border border-gray-100">
      <h3 className="text-xl font-semibold mb-6 text-gray-800 border-b pb-3 flex items-center">
        <Plus className="mr-2 text-blue-600" size={20} />
        {isEditing ? "Modifier le médicament" : "Ajouter un médicament"}
      </h3>

      <div className="space-y-5">
        {/* Numéro d'ordre */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            N° de l'ordonnance
          </label>
          <input
            type="text"
            className="w-full px-3 py-3 bg-gray-100 border border-gray-300 rounded-lg text-gray-600"
            value="Généré automatiquement"
            disabled
          />
        </div>

        {/* Nom du médicament */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Dénomination du médicament *
          </label>
          <input
            type="text"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
            value={currentMedication.name}
            onChange={(e) => handleInputChange(e, "name")}
            placeholder="Ex: Paracétamol, Doliprane..."
          />
        </div>

        {/* Priorité */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Priorité
          </label>
          <select
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={currentMedication.priority}
            onChange={(e) => handleInputChange(e, "priority")}
          >
            <option value="normal">Normal</option>
            <option value="important">Important</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>

        {/* Section Quantité & Dose */}
        <div className="bg-blue-50 rounded-xl p-4 mb-6 shadow-sm">
          <h4 className="text-lg font-semibold text-blue-800 mb-4 flex items-center gap-2">
            <Plus size={20} className="text-blue-500" />
            Quantité & Dose
          </h4>
          <div className="flex flex-col gap-6">
            {/* Quantité */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Quantité
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="1"
                  className="flex-1 px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 transition-all"
                  value={currentMedication.quantity}
                  onChange={(e) => handleInputChange(e, "quantity")}
                />
                <select
                  className="px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 min-w-0 flex-shrink-0"
                  value={currentMedication.quantityUnit}
                  onChange={(e) => handleInputChange(e, "quantityUnit")}
                >
                  {QUANTITY_UNITS.map((unit: QuantityUnit) => (
                    <option key={unit} value={unit}>
                      {unit}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Dose */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Dose
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="1"
                  className="flex-1 px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 transition-all"
                  value={currentMedication.dose}
                  onChange={(e) => handleInputChange(e, "dose")}
                />
                <select
                  className="px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 min-w-0 flex-shrink-0"
                  value={currentMedication.doseUnit}
                  onChange={(e) => handleInputChange(e, "doseUnit")}
                >
                  {DOSE_UNITS.map((unit: DoseUnit) => (
                    <option key={unit} value={unit}>
                      {unit}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Section Fréquence & Intervalle */}
        <div className="bg-blue-50 rounded-xl p-4 mb-6 shadow-sm">
          <h4 className="text-lg font-semibold text-blue-800 mb-4 flex items-center gap-2">
            <Clock size={20} className="text-blue-500" />
            Fréquence & Intervalle
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Fréquence */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Fréquence
              </label>
              <select
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                value={currentMedication.frequency}
                onChange={(e) => handleInputChange(e, "frequency")}
              >
                {FREQUENCIES.map((freq: Frequency) => (
                  <option key={freq} value={freq}>
                    {freq}
                  </option>
                ))}
              </select>
            </div>

            {/* Intervalle */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Intervalle
              </label>
              <select
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                value={currentMedication.interval}
                onChange={(e) => handleInputChange(e, "interval")}
              >
                {INTERVALS.map((interval: Interval) => (
                  <option key={interval} value={interval}>
                    {interval}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Section Durée & Dates */}
        <div className="bg-blue-50 rounded-xl p-4 mb-6 shadow-sm">
          <h4 className="text-lg font-semibold text-blue-800 mb-4 flex items-center gap-2">
            <Calendar size={20} className="text-blue-500" />
            Durée & Dates
          </h4>
          <div className="flex flex-col gap-6">
            {/* Pendant */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Pendant
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="1"
                  className="flex-1 px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  value={currentMedication.duration}
                  onChange={(e) => handleInputChange(e, "duration")}
                />
                <select
                  className="px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 min-w-0 flex-shrink-0"
                  value={currentMedication.durationUnit}
                  onChange={(e) => handleInputChange(e, "durationUnit")}
                >
                  {DURATION_UNITS.map((unit: DurationUnit) => (
                    <option key={unit} value={unit}>
                      {unit}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Date de début */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Date de début
              </label>
              <input
                type="date"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                value={currentMedication.startDate}
                onChange={(e) => handleInputChange(e, "startDate")}
              />
            </div>

            {/* Date de fin */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Date de fin (calculée)
              </label>
              <input
                type="date"
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-600"
                value={currentMedication.endDate}
                onChange={(e) => handleInputChange(e, "endDate")}
              />
            </div>
          </div>
        </div>

        {/* Conseils */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Conseils et précautions
          </label>
          <textarea
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none transition-all"
            rows={4}
            value={currentMedication.advice}
            onChange={(e) => handleInputChange(e, "advice")}
            placeholder="Conseils d'utilisation, précautions, effets secondaires à surveiller..."
          />
        </div>

        {/* Boutons d'action */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          {isEditing ? (
            <>
              <button
                onClick={onSubmit}
                className="flex-1 bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 transition-all duration-200 flex items-center justify-center font-medium"
              >
                <Check className="mr-2" size={18} />
                Mettre à jour
              </button>
              {onCancel && (
                <button
                  onClick={onCancel}
                  className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Annuler
                </button>
              )}
            </>
          ) : (
            <button
              onClick={onSubmit}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-4 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 flex items-center justify-center font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
              disabled={!currentMedication.name.trim()}
            >
              <Plus className="mr-2" size={18} />
              Ajouter à l'ordonnance
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MedicationForm;